import { RealtimeNotice } from "@/shared/components/RealtimeNotice";
import { useSellerRealtime } from "@/shared/realtime";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Button, DiscardChangesDialog, useToast } from "@a2z/ui";
import type {
  SellerAuthSessionResponse,
  SellerAuthView,
  StoreCapability,
  SellerPermission,
} from "@/lib/api/types";
import { keys, getLocaleDirection, type PortalLocale } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { hasDirtyForms } from "@/lib/navigation/dirty-forms";
import { refetchAccessScopedQueries } from "@/shared/services/sessionChannel";
import { Surface } from "@/shared/layout/Surface";
import { LogoutButton } from "@/shared/layout/LogoutButton";
import { FeatureLocked } from "./FeatureLocked";
import { notePortalPath } from "./portal-history";
import { PausedAccess } from "./PausedAccess";
import {
  accessShape,
  canAccess,
  routeAccessRule,
  sellerLanding,
} from "./store-access";

type AccessContextValue = {
  seller: SellerAuthView;
  locale: PortalLocale;
  storeSlug: string;
};
const Context = createContext<AccessContextValue | undefined>(undefined);
export function useStoreAccess() {
  const seller = useContext(Context)?.seller;
  return {
    seller,
    access: seller?.access,
    can: (capability: StoreCapability, permission?: SellerPermission) =>
      canAccess(seller, { capability, permission }),
  };
}
export function SessionUnavailable({
  locale,
  retry,
}: {
  locale: PortalLocale;
  retry: () => unknown;
}) {
  const t = useTranslations();
  return (
    <Surface
      locale={locale}
      direction={getLocaleDirection(locale)}
      title={t(keys.seller.accessUnavailable)}
    >
      <Button onClick={() => void retry()}>{t(keys.seller.accessRetry)}</Button>
      <LogoutButton redirectTo={`/${locale}/login`} />
    </Surface>
  );
}

function relativeStorePath(pathname: string, locale: string, storeSlug: string) {
  return pathname.slice(`/${locale}/${storeSlug}`.length).replace(/^\//, "");
}

/**
 * Admission happens before the shell, route lazy component or its data hooks mount.
 *
 * `routeAdmission="shell"` (the main portal layout) leaves the per-route check
 * to `RouteAdmission`, which the layout renders INSIDE the shell around its
 * outlet — a denied route then explains itself with the nav still there. The
 * full-viewport layouts (editor, quick setup) have no shell and keep the check
 * here.
 */
export function StoreAccessBoundary({
  session,
  locale,
  storeSlug,
  routeAdmission = "here",
  children,
}: {
  session: SellerAuthSessionResponse;
  locale: PortalLocale;
  storeSlug: string;
  routeAdmission?: "here" | "shell";
  children: ReactNode;
}) {
  const location = useLocation();
  const client = useQueryClient();
  const seller = session.seller;
  const realtimeStatus = useSellerRealtime(seller.id, seller.storeId, session.firebaseToken, seller.storeAccess !== false);
  const notice = <RealtimeNotice status={realtimeStatus} onRetry={() => {
    if (realtimeStatus === "unconfigured" || !session.firebaseToken) void client.refetchQueries({
      predicate: query => query.queryKey[0] === "seller" && query.queryKey[1] === seller.storeSlug &&
        (realtimeStatus === "unconfigured" || query.queryKey[2] === "session"),
      type: "active",
    }, { cancelRefetch: false });
  }} />;
  useAccessChangeNotice(seller);
  const shownPath = location.pathname + location.search;
  useEffect(() => notePortalPath(shownPath), [shownPath]);
  if (!seller.access || seller.storeAccess !== true)
    return <>{notice}<PausedAccess session={session} locale={locale} storeSlug={storeSlug} /></>;
  const relativePath = relativeStorePath(location.pathname, locale, storeSlug);
  const landing = sellerLanding(seller);
  if (
    !relativePath ||
    (relativePath === "dashboard" &&
      !canAccess(seller, routeAccessRule(relativePath)))
  )
    return <Navigate to={`/${locale}/${storeSlug}/${landing}`} replace />;
  const value = { seller, locale, storeSlug };
  if (routeAdmission === "here") {
    const rule = routeAccessRule(relativePath + location.search);
    if (!canAccess(seller, rule))
      return (
        <Context.Provider value={value}>
          {notice}
          <main
            dir={getLocaleDirection(locale)}
            lang={locale}
            className="flex min-h-[100svh] items-center bg-[color:var(--canvas)]"
          >
            <FeatureLocked seller={seller} rule={rule} locale={locale} storeSlug={storeSlug} />
          </main>
        </Context.Provider>
      );
  }
  return <Context.Provider value={value}>{notice}{children}</Context.Provider>;
}

/**
 * The per-route gate inside the shell. Admission is decided before the route's
 * lazy page or its data hooks mount, exactly as at the boundary.
 *
 * When an in-place access change closes the page the seller is ON and a form
 * on it holds unsaved edits, the page is not swapped out from under them: a
 * dialog asks first. "Stay" keeps the page (saving will be refused, but the
 * text can be copied out); "Leave" shows the denied screen.
 */
export function RouteAdmission({ children }: { children: ReactNode }) {
  const context = useContext(Context);
  const location = useLocation();
  const path = location.pathname + location.search;
  const seller = context?.seller;
  const rule = context
    ? routeAccessRule(
        relativeStorePath(location.pathname, context.locale, context.storeSlug) +
          location.search,
      )
    : undefined;
  const admitted = canAccess(seller, rule);
  const hold = useDirtyAdmissionHold(path, admitted);

  if (!context) return null;
  if (!hold.showPage)
    return (
      <FeatureLocked
        seller={seller}
        rule={rule}
        locale={context.locale}
        storeSlug={context.storeSlug}
        inShell
      />
    );
  return (
    <>
      {children}
      {hold.dialog}
    </>
  );
}

/**
 * The "you have unsaved edits" hold, shared by `RouteAdmission` (pages inside
 * the shell) and the store editor's full-screen layout
 * (`app/ProtectedEditorLayout.tsx`).
 *
 * When `admitted` turns false while the seller stays on `path` and a form has
 * registered unsaved edits (`lib/navigation/dirty-forms.ts`), the page keeps
 * rendering and `dialog` asks first. "Stay" keeps the page (saving will be
 * refused, but the text can be copied out); "Leave" sets `showPage` false so
 * the caller shows its locked screen.
 */
export function useDirtyAdmissionHold(
  path: string,
  admitted: boolean,
): { showPage: boolean; dialog: ReactNode } {
  const t = useTranslations();
  const [hold, setHold] = useState<{ path: string; mode: "ask" | "stay" | "leave" } | null>(null);
  // What was on screen last render. Compared during render (React's "state
  // from the previous render" pattern) so the dirty check happens BEFORE the
  // page would be swapped out, not in an effect after it is already gone.
  const [shown, setShown] = useState({ path, admitted });
  let mode = hold?.path === path ? hold.mode : null;
  if (shown.path !== path || shown.admitted !== admitted) {
    if (
      !admitted &&
      mode === null &&
      shown.path === path &&
      shown.admitted &&
      hasDirtyForms()
    ) {
      mode = "ask";
      setHold({ path, mode });
    }
    setShown({ path, admitted });
  }
  const showPage = admitted || mode === "ask" || mode === "stay";
  const dialog = !admitted ? (
    <DiscardChangesDialog
      isOpen={mode === "ask"}
      title={t(keys.plan.accessChangedDirtyTitle)}
      body={t(keys.plan.accessChangedDirtyBody)}
      discardLabel={t(keys.plan.accessChangedDirtyLeave)}
      continueLabel={t(keys.plan.accessChangedDirtyStay)}
      onDiscard={() => setHold({ path, mode: "leave" })}
      onContinue={() => setHold({ path, mode: "stay" })}
    />
  ) : null;
  return { showPage, dialog };
}

/**
 * A quiet toast when what this store can do changes underneath an open portal
 * (an admin activated or ended Premium, the owner changed a role). Revision-only
 * changes — renewals, warnings — are not news and stay silent.
 *
 * The same moment refetches the private data. It runs HERE, in an effect of the
 * boundary, because a parent's effects run after every child's: by now each
 * page has rendered with the new access and every Premium-only query
 * (`enabled: can(...)`) has stood down, so the refetch cannot ask the backend
 * for a feature the store just lost.
 */
function useAccessChangeNotice(seller: SellerAuthView) {
  const t = useTranslations();
  const toast = useToast();
  const queryClient = useQueryClient();
  const shape = seller.storeAccess === true && seller.access ? accessShape(seller) : null;
  const previous = useRef(shape);
  useEffect(() => {
    if (previous.current !== null && shape !== null && previous.current !== shape) {
      toast.info(t(keys.plan.accessChangedToast));
      refetchAccessScopedQueries(queryClient);
    }
    previous.current = shape;
  }, [shape, t, toast, queryClient]);
}

export function Capability({
  name,
  permission,
  children,
}: {
  name: StoreCapability;
  permission?: SellerPermission;
  children: ReactNode;
}) {
  const { can } = useStoreAccess();
  return can(name, permission) ? <>{children}</> : null;
}
